import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function SchnupperTerminManager() {
    const { authFetch } = useAuth();
    const [activeTab, setActiveTab] = useState('probewochen');
    const [probewocheBookings, setProbewocheBookings] = useState([]);
    const [kennenlernBookings, setKennenlernBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Schnuppertermine (feste Gruppentermine, 1 Stunde, 5 Euro)
    const [schnupperSlots, setSchnupperSlots] = useState([]);
    const [schnupperBookings, setSchnupperBookings] = useState([]);
    const [schnupperSubTab, setSchnupperSubTab] = useState('slots');
    const [showSlotForm, setShowSlotForm] = useState(false);
    const [editingSlot, setEditingSlot] = useState(null);
    const [slotFormData, setSlotFormData] = useState({
        date: '',
        time: '17:00',
        duration: 60,
        max_participants: 8,
        price: 5,
        title: 'Schnuppertermin',
        notes: ''
    });

    useEffect(() => {
        fetchBookings();
        fetchSchnupperSlots();
        fetchSchnupperBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBookings = async () => {
        setLoading(true);
        try {
            const [probewocheRes, kennenlernRes] = await Promise.all([
                authFetch('/api/probewoche-bookings'),
                authFetch('/api/kennenlernen-bookings')
            ]);

            const probewocheData = await probewocheRes.json();
            const kennenlernData = await kennenlernRes.json();

            setProbewocheBookings(probewocheData.data || []);
            setKennenlernBookings(kennenlernData.data || []);
        } catch (err) {
            setError('Fehler beim Laden der Buchungen');
        } finally {
            setLoading(false);
        }
    };

    const fetchSchnupperSlots = async () => {
        try {
            const res = await authFetch('/api/schnuppertermin-slots');
            const data = await res.json();
            setSchnupperSlots(data.data || []);
        } catch (err) {
            console.error('Fehler beim Laden der Schnuppertermine:', err);
        }
    };

    const fetchSchnupperBookings = async () => {
        try {
            const res = await authFetch('/api/schnuppertermin-bookings');
            const data = await res.json();
            setSchnupperBookings(data.data || []);
        } catch (err) {
            console.error('Fehler beim Laden der Schnuppertermin-Buchungen:', err);
        }
    };

    const resetSlotForm = () => {
        setShowSlotForm(false);
        setEditingSlot(null);
        setSlotFormData({
            date: '',
            time: '17:00',
            duration: 60,
            max_participants: 8,
            price: 5,
            title: 'Schnuppertermin',
            notes: ''
        });
    };

    const handleSlotSubmit = async (e) => {
        e.preventDefault();

        const url = editingSlot
            ? `/api/schnuppertermin-slots/${editingSlot.id}`
            : '/api/schnuppertermin-slots';
        const method = editingSlot ? 'PUT' : 'POST';

        try {
            const res = await authFetch(url, { method, body: slotFormData });
            const data = await res.json();
            if (data.message === 'success') {
                fetchSchnupperSlots();
                resetSlotForm();
            } else {
                alert(data.error || 'Fehler beim Speichern');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const handleSlotEdit = (slot) => {
        setEditingSlot(slot);
        setSlotFormData({
            date: slot.date,
            time: slot.time,
            duration: slot.duration,
            max_participants: slot.max_participants,
            price: slot.price,
            title: slot.title,
            notes: slot.notes || ''
        });
        setShowSlotForm(true);
    };

    const handleSlotDelete = async (id) => {
        if (!window.confirm('Termin wirklich loeschen? Alle Buchungen dazu werden ebenfalls geloescht.')) return;

        try {
            const res = await authFetch(`/api/schnuppertermin-slots/${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.message === 'success') {
                fetchSchnupperSlots();
                fetchSchnupperBookings();
            } else {
                alert(data.error || 'Fehler beim Loeschen');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const updateSchnupperBookingStatus = async (id, status) => {
        try {
            const res = await authFetch(`/api/schnuppertermin-bookings/${id}/status`, {
                method: 'PUT',
                body: { status }
            });
            if (res.ok) {
                fetchSchnupperBookings();
            } else {
                alert('Fehler beim Aktualisieren');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const deleteSchnupperBooking = async (id) => {
        if (!window.confirm('Buchung wirklich loeschen?')) return;

        try {
            const res = await authFetch(`/api/schnuppertermin-bookings/${id}`, { method: 'DELETE' });
            if (res.ok) {
                fetchSchnupperBookings();
                fetchSchnupperSlots();
            } else {
                alert('Fehler beim Loeschen');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const updateStatus = async (type, id, status) => {
        const endpoints = {
            'probewoche': `/api/probewoche-bookings/${id}/status`,
            'kennenlernen': `/api/kennenlernen-bookings/${id}/status`
        };
        const endpoint = endpoints[type];

        try {
            const res = await authFetch(endpoint, {
                method: 'PUT',
                body: { status }
            });

            if (res.ok) {
                fetchBookings();
            } else {
                alert('Fehler beim Aktualisieren');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const deleteBooking = async (type, id) => {
        if (!window.confirm('Buchung wirklich loeschen?')) return;

        const endpoints = {
            'probewoche': `/api/probewoche-bookings/${id}`,
            'kennenlernen': `/api/kennenlernen-bookings/${id}`
        };
        const endpoint = endpoints[type];

        try {
            const res = await authFetch(endpoint, {
                method: 'DELETE'
            });

            if (res.ok) {
                fetchBookings();
            } else {
                alert('Fehler beim Loeschen');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('de-DE', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Formatiert Kennenlerntermin (Format: YYYY-MM-DDTHH:MM oder altes Datum-Format)
    const formatKennenlernTermin = (terminStr) => {
        if (!terminStr) return '-';
        // Neues Format: YYYY-MM-DDTHH:MM
        if (terminStr.includes('T')) {
            const [dateStr, timeStr] = terminStr.split('T');
            const date = new Date(dateStr);
            const dateLabel = date.toLocaleDateString('de-DE', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
            const [hour] = timeStr.split(':').map(Number);
            const endTime = `${String(hour + 1).padStart(2, '0')}:00`;
            return { date: dateLabel, time: `${timeStr} - ${endTime} Uhr` };
        }
        // Altes Format: nur Datum
        const date = new Date(terminStr);
        return {
            date: date.toLocaleDateString('de-DE', {
                weekday: 'short',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }),
            time: '10:00 - 20:00 Uhr'
        };
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': 'bg-warning text-dark',
            'confirmed': 'bg-success',
            'completed': 'bg-info',
            'cancelled': 'bg-danger'
        };
        const labels = {
            'pending': 'Ausstehend',
            'confirmed': 'Bestaetigt',
            'completed': 'Abgeschlossen',
            'cancelled': 'Storniert'
        };
        return (
            <span className={`badge ${badges[status] || 'bg-secondary'}`}>
                {labels[status] || status}
            </span>
        );
    };

    const pendingProbewoche = probewocheBookings.filter(b => b.status === 'pending').length;
    const pendingKennenlernen = kennenlernBookings.filter(b => b.status === 'pending').length;

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laden...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>Schnupper-Pass & Kennenlerntermine</h4>
                <button className="btn btn-outline-primary btn-sm" onClick={fetchBookings}>
                    Aktualisieren
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'probewochen' ? 'active' : ''}`}
                        onClick={() => setActiveTab('probewochen')}
                    >
                        Schnupper-Pass
                        {pendingProbewoche > 0 && (
                            <span className="badge bg-warning text-dark ms-2">{pendingProbewoche}</span>
                        )}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'kennenlerntermine' ? 'active' : ''}`}
                        onClick={() => setActiveTab('kennenlerntermine')}
                    >
                        Kennenlerntermine
                        {pendingKennenlernen > 0 && (
                            <span className="badge bg-warning text-dark ms-2">{pendingKennenlernen}</span>
                        )}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'schnuppertermine' ? 'active' : ''}`}
                        onClick={() => setActiveTab('schnuppertermine')}
                    >
                        Schnuppertermine
                    </button>
                </li>
            </ul>

            {/* Probewochen Tab */}
            {activeTab === 'probewochen' && (
                <div>
                    {probewocheBookings.length === 0 ? (
                        <div className="alert alert-info">Keine Schnupper-Pass-Buchungen vorhanden.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Status</th>
                                        <th>Startdatum</th>
                                        <th>Elternteil</th>
                                        <th>Kind</th>
                                        <th>Kontakt</th>
                                        <th>Gebucht am</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {probewocheBookings.map(booking => (
                                        <tr key={booking.id}>
                                            <td>{getStatusBadge(booking.status)}</td>
                                            <td>
                                                <strong>{formatDate(booking.start_date)}</strong><br />
                                                <span className="badge bg-primary">49 Euro</span>
                                            </td>
                                            <td>{booking.parent_name}</td>
                                            <td>
                                                {booking.child_name}
                                                {booking.child_age && <span className="text-muted"> ({booking.child_age} J.)</span>}
                                            </td>
                                            <td>
                                                <a href={`mailto:${booking.email}`}>{booking.email}</a>
                                                {booking.phone && <><br /><small>{booking.phone}</small></>}
                                            </td>
                                            <td>
                                                <small>{formatDateTime(booking.created_at)}</small>
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm">
                                                    {booking.status === 'pending' && (
                                                        <button
                                                            className="btn btn-success"
                                                            onClick={() => updateStatus('probewoche', booking.id, 'confirmed')}
                                                            title="Bestaetigen"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {booking.status === 'confirmed' && (
                                                        <button
                                                            className="btn btn-info"
                                                            onClick={() => updateStatus('probewoche', booking.id, 'completed')}
                                                            title="Abschliessen"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {booking.status !== 'cancelled' && (
                                                        <button
                                                            className="btn btn-warning"
                                                            onClick={() => updateStatus('probewoche', booking.id, 'cancelled')}
                                                            title="Stornieren"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => deleteBooking('probewoche', booking.id)}
                                                        title="Loeschen"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                                {booking.message && (
                                                    <div className="mt-2">
                                                        <small className="text-muted">
                                                            <strong>Nachricht:</strong> {booking.message}
                                                        </small>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Kennenlerntermine Tab */}
            {activeTab === 'kennenlerntermine' && (
                <div>
                    {kennenlernBookings.length === 0 ? (
                        <div className="alert alert-info">Keine Kennenlerntermin-Buchungen vorhanden.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Status</th>
                                        <th>Wunschtermin 1</th>
                                        <th>Wunschtermin 2</th>
                                        <th>Wunschtermin 3</th>
                                        <th>Elternteil</th>
                                        <th>Kind</th>
                                        <th>Kontakt</th>
                                        <th>Gebucht am</th>
                                        <th>Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kennenlernBookings.map(booking => (
                                        <tr key={booking.id}>
                                            <td>{getStatusBadge(booking.status)}</td>
                                            <td>
                                                <strong>{formatKennenlernTermin(booking.wunschtermin1).date}</strong><br />
                                                <small className="text-muted">{formatKennenlernTermin(booking.wunschtermin1).time}</small>
                                            </td>
                                            <td>
                                                <strong>{formatKennenlernTermin(booking.wunschtermin2).date}</strong><br />
                                                <small className="text-muted">{formatKennenlernTermin(booking.wunschtermin2).time}</small>
                                            </td>
                                            <td>
                                                {booking.wunschtermin3 ? (
                                                    <>
                                                        <strong>{formatKennenlernTermin(booking.wunschtermin3).date}</strong><br />
                                                        <small className="text-muted">{formatKennenlernTermin(booking.wunschtermin3).time}</small>
                                                    </>
                                                ) : '-'}
                                            </td>
                                            <td>{booking.parent_name}</td>
                                            <td>
                                                {booking.child_name || '-'}
                                                {booking.child_age && <span className="text-muted"> ({booking.child_age} J.)</span>}
                                            </td>
                                            <td>
                                                <a href={`mailto:${booking.email}`}>{booking.email}</a>
                                                {booking.phone && <><br /><small>{booking.phone}</small></>}
                                            </td>
                                            <td>
                                                <small>{formatDateTime(booking.created_at)}</small>
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm">
                                                    {booking.status === 'pending' && (
                                                        <button
                                                            className="btn btn-success"
                                                            onClick={() => updateStatus('kennenlernen', booking.id, 'confirmed')}
                                                            title="Bestaetigen"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {booking.status === 'confirmed' && (
                                                        <button
                                                            className="btn btn-info"
                                                            onClick={() => updateStatus('kennenlernen', booking.id, 'completed')}
                                                            title="Abschliessen"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {booking.status !== 'cancelled' && (
                                                        <button
                                                            className="btn btn-warning"
                                                            onClick={() => updateStatus('kennenlernen', booking.id, 'cancelled')}
                                                            title="Stornieren"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-outline-danger"
                                                        onClick={() => deleteBooking('kennenlernen', booking.id)}
                                                        title="Loeschen"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                                {booking.message && (
                                                    <div className="mt-2">
                                                        <small className="text-muted">
                                                            <strong>Nachricht:</strong> {booking.message}
                                                        </small>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Schnuppertermine Tab (feste Gruppentermine, 1 Std., 5 Euro) */}
            {activeTab === 'schnuppertermine' && (
                <div>
                    <ul className="nav nav-pills mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${schnupperSubTab === 'slots' ? 'active' : ''}`}
                                onClick={() => setSchnupperSubTab('slots')}
                            >
                                Termine ({schnupperSlots.length})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${schnupperSubTab === 'bookings' ? 'active' : ''}`}
                                onClick={() => setSchnupperSubTab('bookings')}
                            >
                                Buchungen ({schnupperBookings.length})
                            </button>
                        </li>
                    </ul>

                    {schnupperSubTab === 'slots' && (
                        <>
                            <button className="btn btn-primary mb-3" onClick={() => setShowSlotForm(true)}>
                                + Neuer Schnuppertermin
                            </button>

                            {showSlotForm && (
                                <div className="card mb-4">
                                    <div className="card-body">
                                        <h5>{editingSlot ? 'Termin bearbeiten' : 'Neuer Schnuppertermin'}</h5>
                                        <form onSubmit={handleSlotSubmit}>
                                            <div className="row">
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label">Datum</label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        value={slotFormData.date}
                                                        onChange={e => setSlotFormData({ ...slotFormData, date: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-2 mb-3">
                                                    <label className="form-label">Uhrzeit</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={slotFormData.time}
                                                        onChange={e => setSlotFormData({ ...slotFormData, time: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-2 mb-3">
                                                    <label className="form-label">Dauer (Min)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={slotFormData.duration}
                                                        onChange={e => setSlotFormData({ ...slotFormData, duration: parseInt(e.target.value) })}
                                                        min="15"
                                                        step="15"
                                                    />
                                                </div>
                                                <div className="col-md-2 mb-3">
                                                    <label className="form-label">Max. Teilnehmer</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={slotFormData.max_participants}
                                                        onChange={e => setSlotFormData({ ...slotFormData, max_participants: parseInt(e.target.value) })}
                                                        min="1"
                                                    />
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="form-label">Preis (Euro / Kind)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={slotFormData.price}
                                                        onChange={e => setSlotFormData({ ...slotFormData, price: parseFloat(e.target.value) })}
                                                        min="0"
                                                        step="0.5"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Titel</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={slotFormData.title}
                                                    onChange={e => setSlotFormData({ ...slotFormData, title: e.target.value })}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <label className="form-label">Notizen (intern)</label>
                                                <textarea
                                                    className="form-control"
                                                    rows="2"
                                                    value={slotFormData.notes}
                                                    onChange={e => setSlotFormData({ ...slotFormData, notes: e.target.value })}
                                                />
                                            </div>
                                            <div className="d-flex gap-2">
                                                <button type="submit" className="btn btn-success">
                                                    {editingSlot ? 'Speichern' : 'Erstellen'}
                                                </button>
                                                <button type="button" className="btn btn-secondary" onClick={resetSlotForm}>
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {schnupperSlots.length === 0 ? (
                                <div className="alert alert-info">
                                    Noch keine Schnuppertermine angelegt. Klicke auf "+ Neuer Schnuppertermin".
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Datum</th>
                                                <th>Uhrzeit</th>
                                                <th>Dauer</th>
                                                <th>Preis</th>
                                                <th>Titel</th>
                                                <th>Buchungen</th>
                                                <th>Aktionen</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schnupperSlots.map(slot => (
                                                <tr key={slot.id}>
                                                    <td>{formatDate(slot.date)}</td>
                                                    <td>{slot.time} Uhr</td>
                                                    <td>{slot.duration} Min</td>
                                                    <td>{parseFloat(slot.price).toFixed(2)} Euro</td>
                                                    <td>{slot.title}</td>
                                                    <td>
                                                        <span className={`badge ${slot.booked_count >= slot.max_participants ? 'bg-danger' : slot.booked_count > 0 ? 'bg-warning' : 'bg-success'}`}>
                                                            {slot.booked_count} / {slot.max_participants}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleSlotEdit(slot)}>
                                                            Bearbeiten
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleSlotDelete(slot.id)}>
                                                            Loeschen
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}

                    {schnupperSubTab === 'bookings' && (
                        <>
                            {schnupperBookings.length === 0 ? (
                                <div className="alert alert-info">Keine Schnuppertermin-Buchungen vorhanden.</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Status</th>
                                                <th>Datum/Zeit</th>
                                                <th>Elternteil</th>
                                                <th>Kind</th>
                                                <th>Kontakt</th>
                                                <th>Aktionen</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schnupperBookings.map(booking => (
                                                <tr key={booking.id}>
                                                    <td>{getStatusBadge(booking.status)}</td>
                                                    <td>
                                                        {formatDate(booking.date)}<br />
                                                        <small className="text-muted">{booking.time} Uhr · {parseFloat(booking.price).toFixed(2)} Euro</small>
                                                    </td>
                                                    <td>{booking.parent_name}</td>
                                                    <td>
                                                        {booking.child_name}
                                                        {booking.child_age && <span className="text-muted"> ({booking.child_age} J.)</span>}
                                                    </td>
                                                    <td>
                                                        <a href={`mailto:${booking.email}`}>{booking.email}</a>
                                                        {booking.phone && <><br /><small>{booking.phone}</small></>}
                                                    </td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            {booking.status !== 'cancelled' && (
                                                                <button
                                                                    className="btn btn-warning"
                                                                    onClick={() => updateSchnupperBookingStatus(booking.id, 'cancelled')}
                                                                    title="Stornieren"
                                                                >
                                                                    Stornieren
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn btn-outline-danger"
                                                                onClick={() => deleteSchnupperBooking(booking.id)}
                                                                title="Loeschen"
                                                            >
                                                                Loeschen
                                                            </button>
                                                        </div>
                                                        {booking.message && (
                                                            <div className="mt-2">
                                                                <small className="text-muted"><strong>Nachricht:</strong> {booking.message}</small>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Statistik */}
            <div className="row mt-4 g-3">
                <div className="col-md-4">
                    <div className="card bg-light">
                        <div className="card-body">
                            <h6 className="card-title">Schnupper-Pass</h6>
                            <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-warning text-dark">
                                    {probewocheBookings.filter(b => b.status === 'pending').length} ausstehend
                                </span>
                                <span className="badge bg-success">
                                    {probewocheBookings.filter(b => b.status === 'confirmed').length} bestaetigt
                                </span>
                                <span className="badge bg-info">
                                    {probewocheBookings.filter(b => b.status === 'completed').length} abgeschlossen
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-light">
                        <div className="card-body">
                            <h6 className="card-title">Kennenlerntermine</h6>
                            <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-warning text-dark">
                                    {kennenlernBookings.filter(b => b.status === 'pending').length} ausstehend
                                </span>
                                <span className="badge bg-success">
                                    {kennenlernBookings.filter(b => b.status === 'confirmed').length} bestaetigt
                                </span>
                                <span className="badge bg-info">
                                    {kennenlernBookings.filter(b => b.status === 'completed').length} abgeschlossen
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card bg-light">
                        <div className="card-body">
                            <h6 className="card-title">Schnuppertermine</h6>
                            <div className="d-flex flex-wrap gap-2">
                                <span className="badge bg-success">
                                    {schnupperBookings.filter(b => b.status === 'confirmed').length} bestaetigt
                                </span>
                                <span className="badge bg-secondary">
                                    {schnupperBookings.filter(b => b.status === 'cancelled').length} storniert
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SchnupperTerminManager;
